let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!audioCtx) {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (AudioContextClass) {
        audioCtx = new AudioContextClass();
      }
    }
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  } catch {
    return null;
  }
}

/**
 * Plays a short, rewarding synth beep sound when an item is selected.
 * @param stepIndex Optional index to scale pitch when multiple items are selected in sequence (e.g. during drag).
 */
export function playSelectionBeep(stepIndex: number = 0): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    const baseFreq = 587.33; // D5
    const pitchOffset = (stepIndex % 8) * 45;
    const startFreq = baseFreq + pitchOffset;
    const endFreq = startFreq * 1.2;

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(endFreq, ctx.currentTime + 0.05);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.06);
  } catch {
    // Ignore audio failures when blocked by browser policies
  }
}

/**
 * Plays a quick ascending sequence of beeps when multiple items are added at once (e.g. via shift-select).
 * @param count Number of items added.
 */
export function playSelectionBeepCascade(count: number): void {
  const notesToPlay = Math.min(Math.max(count, 1), 6);
  for (let i = 0; i < notesToPlay; i++) {
    if (i === 0) {
      playSelectionBeep(i);
    } else {
      setTimeout(() => playSelectionBeep(i), i * 30);
    }
  }
}
